/**
 * The engine (CAPABILITIES.md §3.5): find the policy, check the routes the action will call
 * (API_ACCESS.md §3.5), run its visibility requirements, then its enabling ones, and return the
 * first failure. Pure: it never reads the store; the sources do. It fails closed: an id with no
 * policy is hidden and disabled.
 */
import { checkRoutes, type CallDecision, type RefusalCause, type RouteRef } from "./access";
import { createRefusals, type Refusals } from "./refusals";
import type {
  AskContext,
  CapabilityMap,
  Decision,
  EnvFacts,
  Explanation,
  Facts,
  Policy,
  PolicySet,
  Requirement,
  RouteRefusalDrawing,
  ServerCheck,
  SubjectFacts,
  TenantFacts,
} from "./types";

/** one origin of facts: read it (memoised on its inputs), and hear when it changes */
export interface Source<T> {
  read(): T;
  subscribe(listener: () => void): () => void;
}

export interface FactSources {
  subject: Source<SubjectFacts>;
  tenant: Source<TenantFacts>;
  /** everything but the surface, which each question brings (`AskContext`) */
  env: Source<Omit<EnvFacts, "surface">>;
}

type Ask<T> = <K extends keyof CapabilityMap & string>(id: K, resource: CapabilityMap[K], context?: AskContext) => T;

export interface Capabilities {
  can: Ask<Decision>;
  explain: Ask<Explanation>;
  /** may the session call these routes now: the rule, plus what the server refused it (`refusals`) */
  canCall(routes: readonly RouteRef[]): CallDecision;
  /** the server refused this route anyway (a 403): suspend it for the current subject */
  suspend(key: string): boolean;
  /** the current facts, one identity until a source changes (for `useSyncExternalStore`) */
  facts(): Facts;
  subscribe(listener: () => void): () => void;
}

export function definePolicy<R>(
  id: string,
  spec: {
    visibleWhen?: Requirement<R>[];
    enabledWhen?: Requirement<R>[];
    server: ServerCheck;
    notes?: string;
    whenRouteRefused?: RouteRefusalDrawing;
  },
): Policy<R> {
  const policy: Policy<R> = { id, visibleWhen: spec.visibleWhen ?? [], enabledWhen: spec.enabledWhen ?? [], server: spec.server, notes: spec.notes };
  if (spec.whenRouteRefused) policy.whenRouteRefused = spec.whenRouteRefused;
  return policy;
}

/** a domain file's policies; every id must start with `<domain>.` (§3.4) */
export function definePolicies(domain: string, policies: Policy<never>[]): PolicySet {
  return { domain, policies };
}

/** one id, one rule (the user, 2026-10-05): two policies for an id, or an id outside its domain, throw */
export function indexPolicies(sets: readonly PolicySet[]): ReadonlyMap<string, Policy> {
  const index = new Map<string, Policy>();
  for (const { domain, policies } of sets) {
    for (const policy of policies) {
      if (!policy.id.startsWith(`${domain}.`)) throw new Error(`capabilities: "${policy.id}" is not in the ${domain} domain`);
      if (index.has(policy.id)) throw new Error(`capabilities: two policies claim "${policy.id}"`);
      index.set(policy.id, policy as Policy);
    }
  }
  return index;
}

const UNKNOWN: Decision = { visible: false, enabled: false, denial: { requirement: "policy", reason: "capabilities.unknown" } };
const ALLOWED: Decision = { visible: true, enabled: true };

const firstFailure = (requirements: readonly Requirement[], facts: Facts, resource: unknown) =>
  requirements.find((req) => !req.test(facts, resource));

/**
 * QR1 (decided 2026-10-06, by cause): a session that can never call the route (no module, or not
 * known yet) does not see the action; one that lacks the role, or whose route the server suspended,
 * sees it disabled, with the reason, as for a missing permission.
 */
const DRAWING_BY_CAUSE: Record<RefusalCause, RouteRefusalDrawing> = {
  session: "hide",
  module: "hide",
  role: "disable",
  suspended: "disable",
};

/** "route:POST /sale/save", the requirement id a route refusal is explained under */
const routeRequirement = (key: string) => `route:${key}`;

/** a failed visibility requirement hides the control; a failed enabling one disables it, with its remedy */
function decide(policy: Policy, facts: Facts, resource: unknown, calls: CallDecision): Decision {
  if (!calls.allowed) {
    const drawing = policy.whenRouteRefused ?? DRAWING_BY_CAUSE[calls.cause];
    return { visible: drawing === "disable", enabled: false, denial: { requirement: routeRequirement(calls.route), reason: calls.reason } };
  }
  const hiding = firstFailure(policy.visibleWhen, facts, resource);
  if (hiding) {
    return { visible: false, enabled: false, denial: { requirement: hiding.id, reason: hiding.reason } };
  }
  const disabling = firstFailure(policy.enabledWhen, facts, resource);
  if (disabling) {
    const decision: Decision = { visible: true, enabled: false, denial: { requirement: disabling.id, reason: disabling.reason } };
    if (disabling.remedy) decision.remedy = disabling.remedy;
    return decision;
  }
  return ALLOWED;
}

/** every route and requirement of both phases and whether it passed, for logs and readable test failures */
function checksOf(policy: Policy, facts: Facts, resource: unknown, routes: Explanation["checks"]): Explanation["checks"] {
  const visible = policy.visibleWhen.map((req) => ({ phase: "visible" as const, requirement: req.id, passed: req.test(facts, resource) }));
  const enabled = policy.enabledWhen.map((req) => ({ phase: "enabled" as const, requirement: req.id, passed: req.test(facts, resource) }));
  return [...routes, ...visible, ...enabled];
}

export function createCapabilities({
  policies,
  sources,
  refusals = createRefusals(),
}: {
  policies: readonly PolicySet[];
  sources: FactSources;
  /** what the server refused this session anyway (API_ACCESS.md §3.6); each engine has its own by default */
  refusals?: Refusals;
}): Capabilities {
  const index = indexPolicies(policies);
  const warned = new Set<string>();
  /** bumped when a route is suspended, so the snapshot below changes identity and asks again */
  let refusalVersion = 0;
  refusals.subscribe(() => void refusalVersion++);
  let last: { subject: SubjectFacts; tenant: TenantFacts; env: Omit<EnvFacts, "surface">; version: number; facts: Facts } | null = null;

  const facts = (): Facts => {
    const subject = sources.subject.read();
    const tenant = sources.tenant.read();
    const env = sources.env.read();
    if (!last || last.subject !== subject || last.tenant !== tenant || last.env !== env || last.version !== refusalVersion) {
      last = { subject, tenant, env, version: refusalVersion, facts: { subject, tenant, env: { ...env, surface: "unknown" } } };
    }
    return last.facts;
  };

  /** the routes in order: the backend's rule, then what the server refused this subject */
  const callsFor = (routes: readonly RouteRef[], subject: SubjectFacts): { decision: CallDecision; checks: Explanation["checks"] } => {
    const { decision, each } = checkRoutes(routes, subject, (key) => refusals.isSuspended(key, subject));
    return { decision, checks: each.map((e) => ({ phase: "routes" as const, requirement: routeRequirement(e.key), passed: e.decision.allowed })) };
  };

  const asked = (context?: AskContext): Facts => {
    const base = facts();
    return context?.surface ? { ...base, env: { ...base.env, surface: context.surface } } : base;
  };

  const policyFor = (id: string): Policy | null => {
    const policy = index.get(id) ?? null;
    if (!policy && !warned.has(id) && typeof process !== "undefined" && process.env?.NODE_ENV !== "production") {
      warned.add(id);
      console.warn(`capabilities: no policy for "${id}", so it is hidden`);
    }
    return policy;
  };

  return {
    can(id, resource, context) {
      const policy = policyFor(id);
      if (!policy) return UNKNOWN;
      const facts = asked(context);
      return decide(policy, facts, resource, callsFor(context?.routes ?? [], facts.subject).decision);
    },
    explain(id, resource, context) {
      const policy = policyFor(id);
      if (!policy) return { id, decision: UNKNOWN, checks: [] };
      const facts = asked(context);
      const calls = callsFor(context?.routes ?? [], facts.subject);
      return { id, decision: decide(policy, facts, resource, calls.decision), checks: checksOf(policy, facts, resource, calls.checks) };
    },
    canCall: (routes) => callsFor(routes, facts().subject).decision,
    suspend: (key) => refusals.suspend(key, facts().subject),
    facts,
    subscribe(listener) {
      const offs = [
        sources.subject.subscribe(listener),
        sources.tenant.subscribe(listener),
        sources.env.subscribe(listener),
        refusals.subscribe(listener),
      ];
      return () => offs.forEach((off) => off());
    },
  };
}
